import React from 'react';
import * as yup from 'yup';

import { TextInput } from '@producer/ui-kit';

import { Select } from '../../components/select/Select';

import type { FormBuilderDefinition } from '../../components/form-builder-modal/form-builder';

const genderOptions = [
  { value: 'man', label: 'Man' },
  { value: 'woman', label: 'Woman' },
  { value: 'other', label: 'Other' },
];

export type CharacterFormSibling = {
  _id: string;
  name: string;
  characterNumber?: number;
};

export const buildCharacterForm: (
  isCreate: boolean,
  handleSetImage: (file?: File) => void,
  siblings?: CharacterFormSibling[],
) => FormBuilderDefinition = (
  isCreate: boolean,
  handleSetImage: (file?: File) => void,
  siblings: CharacterFormSibling[] = [],
) => ({
  header: isCreate ? 'Create character' : 'Edit character',
  submitText: isCreate ? 'Create character' : 'Edit character',
  fields: [
    {
      type: 'upload',
      handleSetImage,
    },
    {
      type: 'group',
      sectionHeader: 'Character information',
    },
    {
      name: 'characterNumber',
      type: 'controller',
      validation: yup
        .number()
        .transform((value, original) => (original === '' || original === null ? undefined : value))
        .typeError('Number must be a whole number')
        .integer('Number must be a whole number')
        .min(1, 'Number must be 1 or more'),
      render: ({ field, fieldState: { error } }) => {
        // Duplicates are deliberately allowed (PRO-1083) — this is a hint, never a blocked save.
        const alsoUsedBy = siblings
          .filter(
            (sibling) =>
              sibling.characterNumber !== undefined &&
              `${sibling.characterNumber}` === `${field.value ?? ''}`,
          )
          .map((sibling) => sibling.name);

        return (
          <TextInput
            {...field}
            value={field.value ?? ''}
            type="number"
            label="Number"
            placeholder="Number"
            hasError={!!error}
            errorMessage={error?.message}
            hint={alsoUsedBy.length ? `Also used by ${alsoUsedBy.join(', ')}` : undefined}
          />
        );
      },
    },
    {
      name: 'name',
      label: 'Character name',
      placeholder: 'Character name',
      isRequired: true,
      type: 'text',
      validation: yup
        .string()
        .matches(/^(.*)?\S+(.*)?$/, 'Name is required')
        .required('Name is required')
        .max(32, 'Max 32 characters'),
    },
    {
      name: 'gender',
      type: 'controller',
      render: ({ field, fieldState: { error } }) => (
        <Select
          {...field}
          onSelect={({ target }) => field.onChange(target.value)}
          options={genderOptions}
          label={'Gender'}
          hasError={!!error}
          errorMessage={error?.message}
        />
      ),
    },
    {
      name: 'description',
      label: 'Description',
      placeholder: 'Description',
      type: 'textArea',
      validation: yup.string().max(1024, 'Max 1024 characters'),
    },
    {
      name: 'personality',
      label: 'Personality',
      placeholder: 'Personality',
      type: 'textArea',
      validation: yup.string().max(1024, 'Max 1024 characters'),
    },
    {
      name: 'characteristics',
      label: 'Physical characteristics',
      placeholder: 'Physical characteristics',
      type: 'textArea',
      validation: yup.string().max(1024, 'Max 1024 characters'),
    },
    {
      name: 'appearance',
      label: 'Appearance',
      placeholder: 'Appearance',
      type: 'textArea',
      validation: yup.string().max(1024, 'Max 1024 characters'),
    },
  ],
});
